// Placements that compete for a single on-screen slot (page-specific placements are independent).
export const GLOBAL_EXCLUSIVE_PLACEMENTS = [
  'modal',
  'home-banner',
  'chat-banner',
  'sidebar',
  'tooltip',
];

export const PLACEMENT_PRIORITY = {
  modal: 100,
  'home-banner': 90,
  'chat-banner': 85,
  sidebar: 80,
  tooltip: 70,
  'credit-store': 60,
  'upgrade-page': 50,
};

/**
 * Whether `PromoBanner` should render its CTA button.
 *
 * Inside the credit store the promo pill already acts as the CTA, so a second
 * button that reopens the store the user is standing in is noise — but only if
 * there is a pill. A credit-store banner with no promo code and no CTA button
 * has nothing pressable at all, which is how "Credit Buyer → Plus" collected
 * 249 impressions and zero clicks.
 */
export function shouldShowCtaButton(banner) {
  if (!banner?.content?.ctaText) return false;
  if (banner.placement !== 'credit-store') return true;
  return !banner?.promoCode?.code;
}

function passesPlacementCooldown(placement, coolingDown) {
  if (placement === 'tooltip') return true;
  if (coolingDown(placement)) return false;
  if ((placement === 'home-banner' || placement === 'chat-banner') && coolingDown('_post_modal')) {
    return false;
  }
  return true;
}

export function pickActiveGlobalPlacement(banners, coolingDown = () => false) {
  let bestPlacement = null;
  let bestScore = -1;

  for (const placement of GLOBAL_EXCLUSIVE_PLACEMENTS) {
    if (!passesPlacementCooldown(placement, coolingDown)) continue;

    const topBanner = banners
      .filter(b => b.placement === placement && (placement !== 'tooltip' || b.tooltipAnchor))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0))[0];

    if (!topBanner) continue;

    const score = (PLACEMENT_PRIORITY[placement] || 0) * 1000 + (topBanner.priority || 0);
    if (score > bestScore) {
      bestScore = score;
      bestPlacement = placement;
    }
  }

  return bestPlacement;
}

export function filterBannersForPlacement(
  banners,
  placement,
  activeGlobalPlacement,
  coolingDown = () => false,
) {
  if (GLOBAL_EXCLUSIVE_PLACEMENTS.includes(placement) && activeGlobalPlacement !== placement) {
    return [];
  }

  if (placement !== 'tooltip' && !passesPlacementCooldown(placement, coolingDown)) {
    return [];
  }

  return banners
    .filter(b => b.placement === placement)
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

function isPricingCta(ctaUrl) {
  if (!ctaUrl || typeof ctaUrl !== 'string') return false;
  const url = ctaUrl.trim().toLowerCase();
  return url === 'action:open_pricing'
    || url === 'action:open_subscription'
    || url.startsWith('modal:pricingmodal')
    || url === '/pricing'
    || url.startsWith('/pricing?')
    || /[?&]checkout=plus(?:&|$)/.test(url);
}

/**
 * Client-side strip of go-Plus banners. The server is the authority; this
 * covers the 5-minute hook cache and any payload that predated the gate.
 * Subscription promos are always go-Plus. Pricing CTAs without a server
 * `hideFromPlus` flag are left alone so monthly→annual still renders.
 */
export function looksLikeGoPlusBanner(banner) {
  if (banner?.hideFromPlus) return true;
  const promoType = banner?.promoCode?.promoType;
  if (promoType === 'subscription') return true;
  if (
    banner?.contentType === 'promo-code'
    && banner?.placement !== 'credit-store'
    && promoType !== 'credit-pack'
  ) {
    return true;
  }
  return false;
}

export function filterGoPlusBanners(banners, flags = {}) {
  const isComped = !!flags.isComped;
  const isPlus = !!(flags.isPlus || flags.isPlusPremium || isComped);
  if (!isPlus && !isComped) return banners;
  return banners.filter((b) => {
    if (isComped && isPricingCta(b?.content?.ctaUrl)) return false;
    if (isPlus && looksLikeGoPlusBanner(b)) return false;
    return true;
  });
}
